import {
  Box,
  Typography,
  IconButton,
  CardContent,
  Collapse,
  Link,
} from "@mui/material";
import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import ExpandMoreIcon from "@mui/icons-material/ExpandMoreRounded";
import PlayCircleRoundedIcon from "@mui/icons-material/PlayCircleRounded";
import { useTheme } from "@mui/material/styles";
import TeamIcon from "../../assets/icons/team-purple.svg";
import LoadingIcon from "../../assets/icons/loading.svg";
import CheckIcon from "../../assets/icons/check.svg";
import DefaultBadge from "../simple/DefaultBadge";
import MenuBookRoundedIcon from "@mui/icons-material/MenuBookRounded";
import DefaultIconBadge from "../simple/DefaultIconBadge";
import DefaultButton from "../simple/DefaultButton";
import { ArrowForwardRounded } from "@mui/icons-material";
import CircularProgress from "@mui/material/CircularProgress";

export default function ProgramTopicsDropdown({
  chapter,
  allScenariosCompleted,
  percentageCompleted,
  expandedStates,
  setExpandedStates,
  setAllOpen,
  allChapterIds,
  locked,
  isMobile,
}) {
  const theme = useTheme();
  const navigate = useNavigate();

  const [programLoading, setProgramLoading] = React.useState(false);
  const [scoringStats, setScoringStats] = React.useState({
    withScoring: 0,
    withoutScoring: 0,
  });

  useEffect(() => {
    if (!chapter) return;
    if (!chapter.scenarios) return;

    let withScoring = 0;
    let withoutScoring = 0;

    chapter.scenarios.forEach((scenario) => {
      if (scenario?.has_scoring_engine) {
        withScoring++;
      } else {
        withoutScoring++;
      }
    });

    setScoringStats({ withScoring, withoutScoring });
  }, [chapter]);

  const handleExpandClick = (id) => {
    setExpandedStates((prev) => {
      const newState = {
        ...prev,
        [id]: !prev[id],
      };

      const areAllNowOpen = allChapterIds.every(
        (chapterId) => newState[chapterId]
      );
      setAllOpen(areAllNowOpen);

      return newState;
    });
  };

  const isExpanded = !!expandedStates[chapter.id];

  return (
    <Box
      key={chapter.id}
      sx={{
        position: "relative",
        display: "flex",
        m: `${theme.customSpacing.xl} 0`,
        flexDirection: "row",
        flexGrow: 1,
        backgroundColor: "transparent",
        justifyContent: "space-between",
        paddingBottom: "0px !important",
      }}
    >
      <IconButton
        onClick={() => handleExpandClick(chapter.id)}
        sx={{ margin: `0`, padding: 0, position: "absolute", top: "-2px" }}
      >
        <ExpandMoreIcon
          sx={{
            color: theme.palette.foreground.button_secondary,
            fontSize: "24px",
            transition: "transform 0.3s ease-in-out",
            transform: isExpanded ? "rotate(180deg)" : "rotate(0deg)",
          }}
        />
      </IconButton>
      <CardContent
        sx={{
          margin: 0,
          padding: 0,
          flexGrow: 1,
          paddingBottom: "0px !important",
          "& .program-loading": {
            display: "flex",
            flexDirection: "column",
            textAlign: "center",
            justifyContent: "center",
            alignItems: "center",
            gap: "10px",
            fontSize: "14px",
            padding: "50px 0",
            "& span": {
              opacity: ".8"
            }
          },
        }}
      >
        <Typography
          sx={{
            cursor: "pointer",
            opacity: locked ? 0.4 : 1,
            color: theme.palette.text.primary,
            fontSize: isMobile ? "16px" : theme.typography.textLG.fontSize,
            fontWeight: theme.typography.fontWeightMedium,
            lineHeight: "1.2",
            ml: theme.customSpacing["4xl"],
            maxWidth: isMobile ? "80%" : "70%",
          }}
          onClick={() => handleExpandClick(chapter.id)}
        >
          {chapter.name}
        </Typography>
        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
          <Typography
            sx={{
              margin: `${theme.customSpacing.xl} 0`,
              color: theme.palette.text.tertiary,
              fontSize: theme.typography.textSM.fontSize,
              fontWeight: theme.typography.fontWeightRegular,
              lineHeight: theme.typography.textSM.lineHeight,
              opacity: ".8",
            }}
          >
            {chapter.description}
          </Typography>
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: "5px",
              margin: "0 0 20px 0",
              borderBottom: "rgba(255, 255, 255, .2) 1px dashed",
              paddingBottom: "20px",

              "& .badge-container": {
                "& span": {
                  width: "auto",
                  height: "auto",
                  fontWeight: "normal",
                },
                "& svg": {
                  width: 15,
                  height: 15,
                  marginRight: "5px",
                },
              },
            }}
          >
            <DefaultBadge
              children={`${scoringStats.withoutScoring} Skill Development Sessions`}
              size="large"
              iconColor={theme.palette.text.badge_icon}
              className="badge-container"
              sx={{
                border: `#01ECFF 1px solid`,
              }}
              startIcon={
                <svg
                  viewBox="0 0 12 12"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M6 10.5L5.94997 10.425C5.60265 9.90398 5.42899 9.64349 5.19955 9.45491C4.99643 9.28796 4.76238 9.1627 4.5108 9.0863C4.22663 9 3.91355 9 3.28741 9H2.6C2.03995 9 1.75992 9 1.54601 8.89101C1.35785 8.79513 1.20487 8.64215 1.10899 8.45399C1 8.24008 1 7.96005 1 7.4V3.1C1 2.53995 1 2.25992 1.10899 2.04601C1.20487 1.85785 1.35785 1.70487 1.54601 1.60899C1.75992 1.5 2.03995 1.5 2.6 1.5H2.8C3.9201 1.5 4.48016 1.5 4.90798 1.71799C5.28431 1.90973 5.59027 2.21569 5.78201 2.59202C6 3.01984 6 3.5799 6 4.7M6 10.5V4.7M6 10.5L6.05003 10.425C6.39735 9.90398 6.57101 9.64349 6.80045 9.45491C7.00357 9.28796 7.23762 9.1627 7.4892 9.0863C7.77337 9 8.08645 9 8.71259 9H9.4C9.96005 9 10.2401 9 10.454 8.89101C10.6422 8.79513 10.7951 8.64215 10.891 8.45399C11 8.24008 11 7.96005 11 7.4V3.1C11 2.53995 11 2.25992 10.891 2.04601C10.7951 1.85785 10.6422 1.70487 10.454 1.60899C10.2401 1.5 9.96005 1.5 9.4 1.5H9.2C8.07989 1.5 7.51984 1.5 7.09202 1.71799C6.71569 1.90973 6.40973 2.21569 6.21799 2.59202C6 3.01984 6 3.5799 6 4.7"
                    stroke="#01ECFF"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
              }
              variant="pillTransparent"
            />

            <DefaultBadge
              children={`${scoringStats.withScoring} Simulation Challenges`}
              size="large"
              className="badge-container"
              iconColor={theme.palette.text.badge_icon}
              sx={{
                border: `#A87EFF 1px solid`,
              }}
              startIcon={
                <svg
                  viewBox="0 0 12 12"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M11 10.5V9.5C11 8.56808 10.3626 7.78503 9.5 7.56301M7.75 1.64538C8.48296 1.94207 9 2.66066 9 3.5C9 4.33934 8.48296 5.05793 7.75 5.35462M8.5 10.5C8.5 9.56812 8.5 9.10218 8.34776 8.73463C8.14477 8.24458 7.75542 7.85523 7.26537 7.65224C6.89782 7.5 6.43188 7.5 5.5 7.5H4C3.06812 7.5 2.60218 7.5 2.23463 7.65224C1.74458 7.85523 1.35523 8.24458 1.15224 8.73463C1 9.10218 1 9.56812 1 10.5M6.75 3.5C6.75 4.60457 5.85457 5.5 4.75 5.5C3.64543 5.5 2.75 4.60457 2.75 3.5C2.75 2.39543 3.64543 1.5 4.75 1.5C5.85457 1.5 6.75 2.39543 6.75 3.5Z"
                    stroke="#A87EFF"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
              }
              variant="pillTransparent"
            />
          </Box>

          {programLoading ? (
            <div className="program-loading">
              <CircularProgress enableTrackSlot />
              <span>Loading...</span>
            </div>
          ) : (
            <Box className="chapters-container" sx={{ padding: "15px" }}>
              {chapter.scenarios &&
                chapter.scenarios.length > 0 &&
                chapter.scenarios.map((scenario, i) => (
                  <Link
                    key={scenario?.id}
                    onClick={() => {
                      if (!scenario.locked) {
                        setProgramLoading(true);
                        navigate(`/dashboard/simulate/${scenario.id}`);
                      }
                    }}
                    sx={{
                      opacity: scenario.locked ? 0.5 : 1,
                      // color: scenario.completed
                      //   ? theme.palette.text.primary
                      //   : scenario.locked
                      //   ? theme.palette.text.primary
                      //   : theme.palette.foreground.badge_icon,
                      color: "#fff",
                      textDecoration: "none",
                      mb:
                        i === chapter.scenarios.length - 1
                          ? 0
                          : theme.customSpacing.xl,
                      cursor: !scenario.locked && "pointer",
                      display: "block",
                    }}
                  >
                    <Box
                      className="chapter-step-item"
                      sx={{
                        display: "flex",
                        flexDirection: "row",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "row",
                          alignItems: "center",
                          gap: theme.customSpacing.md,
                        }}
                      >
                        {scenario.has_scoring_engine ? (
                          <svg
                            width="15"
                            height="15"
                            viewBox="0 0 12 12"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                            style={{
                              stroke: scenario.completed ? "#fff" : "#A87EFF",
                            }}
                          >
                            <path
                              d="M11 10.5V9.5C11 8.56808 10.3626 7.78503 9.5 7.56301M7.75 1.64538C8.48296 1.94207 9 2.66066 9 3.5C9 4.33934 8.48296 5.05793 7.75 5.35462M8.5 10.5C8.5 9.56812 8.5 9.10218 8.34776 8.73463C8.14477 8.24458 7.75542 7.85523 7.26537 7.65224C6.89782 7.5 6.43188 7.5 5.5 7.5H4C3.06812 7.5 2.60218 7.5 2.23463 7.65224C1.74458 7.85523 1.35523 8.24458 1.15224 8.73463C1 9.10218 1 9.56812 1 10.5M6.75 3.5C6.75 4.60457 5.85457 5.5 4.75 5.5C3.64543 5.5 2.75 4.60457 2.75 3.5C2.75 2.39543 3.64543 1.5 4.75 1.5C5.85457 1.5 6.75 2.39543 6.75 3.5Z"
                              stroke-linecap="round"
                              stroke-linejoin="round"
                            />
                          </svg>
                        ) : (
                          <svg
                            width="15"
                            height="15"
                            viewBox="0 0 12 12"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                            style={{
                              stroke: scenario.completed ? "#fff" : "#01ECFF",
                            }}
                          >
                            <path
                              d="M6 10.5L5.94997 10.425C5.60265 9.90398 5.42899 9.64349 5.19955 9.45491C4.99643 9.28796 4.76238 9.1627 4.5108 9.0863C4.22663 9 3.91355 9 3.28741 9H2.6C2.03995 9 1.75992 9 1.54601 8.89101C1.35785 8.79513 1.20487 8.64215 1.10899 8.45399C1 8.24008 1 7.96005 1 7.4V3.1C1 2.53995 1 2.25992 1.10899 2.04601C1.20487 1.85785 1.35785 1.70487 1.54601 1.60899C1.75992 1.5 2.03995 1.5 2.6 1.5H2.8C3.9201 1.5 4.48016 1.5 4.90798 1.71799C5.28431 1.90973 5.59027 2.21569 5.78201 2.59202C6 3.01984 6 3.5799 6 4.7M6 10.5V4.7M6 10.5L6.05003 10.425C6.39735 9.90398 6.57101 9.64349 6.80045 9.45491C7.00357 9.28796 7.23762 9.1627 7.4892 9.0863C7.77337 9 8.08645 9 8.71259 9H9.4C9.96005 9 10.2401 9 10.454 8.89101C10.6422 8.79513 10.7951 8.64215 10.891 8.45399C11 8.24008 11 7.96005 11 7.4V3.1C11 2.53995 11 2.25992 10.891 2.04601C10.7951 1.85785 10.6422 1.70487 10.454 1.60899C10.2401 1.5 9.96005 1.5 9.4 1.5H9.2C8.07989 1.5 7.51984 1.5 7.09202 1.71799C6.71569 1.90973 6.40973 2.21569 6.21799 2.59202C6 3.01984 6 3.5799 6 4.7"
                              stroke-linecap="round"
                              stroke-linejoin="round"
                            />
                          </svg>
                        )}
                        <Typography sx={{ fontSize: "14px" }}>
                          {scenario?.name}
                        </Typography>
                      </Box>
                      {!scenario.locked &&
                        (scenario.completed !== true ? (
                          <DefaultButton
                            size="sm"
                            backgroundColor={"transparent"}
                            textColor={theme.palette.text.primary}
                            borderColor={theme.palette.border.brand}
                            endIcon={
                              <ArrowForwardRounded
                                sx={{
                                  fontSize: theme.typography.textSM.fontSize,
                                }}
                              />
                            }
                            onClick={() => {
                              if (!scenario.locked) {
                                navigate(`/dashboard/simulate/${scenario.id}`);
                              }
                            }}
                            sx={{
                              "& p": {
                                display: isMobile ? "none" : "block",
                              },
                              "& svg": {
                                [theme.breakpoints.down("sm")]: {
                                  width: "20px",
                                  height: "20px",
                                },
                              },
                            }}
                          >
                            {!isMobile ? "Get Started" : " "}
                          </DefaultButton>
                        ) : (
                          <DefaultIconBadge
                            variant="iconBadgeSuccess"
                            icon={<img src={CheckIcon} alt="" />}
                          />
                        ))}
                    </Box>
                  </Link>
                ))}
            </Box>
          )}
        </Collapse>
      </CardContent>
      {!locked &&
        (allScenariosCompleted ? (
          <Box sx={{ right: "0", position: "absolute" }}>
            <DefaultBadge
              variant="pillSuccess"
              startIcon={<img src={CheckIcon} alt="" />}
              children={`${!isMobile ? " Completed" : ""}`}
              size="small"
              sx={{ alignSelf: "center", height: "auto" }}
            />
          </Box>
        ) : (
          <Box sx={{ right: "0", position: "absolute" }}>
            <DefaultBadge
              variant="pillTransparent"
              startIcon={<img src={LoadingIcon} alt="Loading" />}
              borderColor={theme.palette.foreground.badge_icon}
              children={`${percentageCompleted}%${
                !isMobile ? " Completed" : ""
              }`}
              size="small"
              sx={{ alignSelf: "center", height: "auto" }}
            />
          </Box>
        ))}
    </Box>
  );
}
